// A marca do patrocinador da liga: faixa da liga, verso das cartas, o espaço no feltro e a tela de entrada de 3 s.
// Só visual, sem estado nem medição: o painel admin importa estas peças para a prévia da liga (apps/admin).
// Sem patrocinador, a faixa e o feltro mostram "Anuncie Aqui", o verso é o da casa e a tela de entrada não aparece.
import type { ReactNode } from 'react';
import type { LigaDaSala } from '@truco6/mesa/protocolo';
import { cores, type OpcoesVerso, VERSO_DA_CASA } from '@truco6/ui';
import './marca.css';

// Verso das cartas da mesa: o da liga patrocinada, com a cor e a sigla da marca; senão, o da casa.
export function versoDaLiga(liga: LigaDaSala | null | undefined): OpcoesVerso {
  const m = liga?.marca;
  if (!m) return VERSO_DA_CASA;
  const sigla = m.nome.split(/\s+/).filter(Boolean).map((p) => p[0]!.toUpperCase()).join('').slice(0, 3) || 'L';
  return { base: m.cor ?? cores.sponsor500, line: cores.sponsor300, mono: sigla, monoFill: cores.sponsor300, monoInk: cores.ink900 };
}

// A cor da marca entra no lugar do token sponsor-500, que é o espaço do patrocinador no design system.
export function estiloDaMarca(liga: LigaDaSala | null | undefined): React.CSSProperties | undefined {
  return liga?.marca?.cor ? ({ '--sponsor-500': liga.marca.cor } as React.CSSProperties) : undefined;
}

// Tempo até o fim da liga: "2d 14h", "5h 12min" ou "12min".
export function tempoAte(fim: number, agora: number): string {
  const min = Math.max(0, Math.floor((fim - agora) / 60_000)), h = Math.floor(min / 60), d = Math.floor(h / 24);
  if (d > 0) return `${d}d ${h % 24}h`;
  if (h > 0) return `${h}h ${min % 60}min`;
  return `${min}min`;
}

// "12 a 18 de outubro" (ou "28 de setembro a 4 de outubro"), no horário de Brasília.
export function periodoDaSemana(inicio: number, fim: number): string {
  const f = (ms: number, mes: boolean) => new Date(ms).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo', day: 'numeric', ...(mes ? { month: 'long' } : {}) });
  const mesmoMes = f(inicio, true).split(' de ')[1] === f(fim, true).split(' de ')[1];
  return `${mesmoMes ? f(inicio, false) : f(inicio, true)} a ${f(fim, true)}`;
}

// Faixa da liga (FaixaLiga do design system): nome da liga, o espaço da marca e, à direita, um chip.
// Sem patrocinador, o espaço da marca mostra "Anuncie Aqui".
export function FaixaLiga({ liga, chip, acao }: { liga: LigaDaSala; chip?: ReactNode; acao?: () => void }) {
  const conteudo = (
    <>
      <span className="faixa-liga-nome">
        <IconeTrofeu />
        <span>{liga.nome}</span>
        <span className="brand">{liga.marca ? liga.marca.nome : 'Anuncie Aqui'}</span>
      </span>
      {chip && <span className="faixa-liga-chip">{chip}</span>}
    </>
  );
  return acao
    ? <button className="faixa-liga" style={estiloDaMarca(liga)} onClick={acao}>{conteudo}</button>
    : <div className="faixa-liga" style={estiloDaMarca(liga)}>{conteudo}</div>;
}

// O espaço da marca no feltro (com até 10% de opacidade, no CSS de quem desenha a mesa): o logo, o nome ou "Anuncie Aqui".
// No centro do feltro, o "Anuncie" e o "Aqui" ficam dos dois lados do monte, inteiros; o logo e o nome, atrás dele.
export function MarcaNoFeltro({ marca }: { marca: LigaDaSala['marca'] }) {
  if (marca?.logo) return <img className="inteira" src={marca.logo} alt="" />;
  if (marca) return <span className="inteira">{marca.nome}</span>;
  return <><span className="lado-esq">Anuncie</span>{' '}<span className="lado-dir">Aqui</span></>;
}

// Tela de entrada da liga patrocinada (canvas B01): 3 s antes das cartas da primeira mão de cada partida.
// Sem patrocinador, ela não aparece.
export function EntradaPatrocinada({ liga, ms, semana }: {
  liga: LigaDaSala; ms: number; semana?: { inicio: number; fim: number; premio: string | null; agora: number } | null;
}) {
  const m = liga.marca;
  if (!m) return null;
  return (
    <div className="entrada-patrocinada" style={estiloDaMarca(liga)} role="dialog" aria-modal="true" aria-label={`${liga.nome}, oferecida por ${m.nome}`}>
      <div className="entrada-marca">
        <span className="entrada-oferece">Esta mesa é oferecida por</span>
        <div className="entrada-logo">{m.logo ? <img src={m.logo} alt="" /> : <b aria-hidden="true">{versoDaLiga(liga).mono}</b>}</div>
        <h1>{m.nome}</h1>
        <p>{liga.nome}{semana && <> · {periodoDaSemana(semana.inicio, semana.fim)}</>}</p>
        {semana && <span className="entrada-fim">termina em {tempoAte(semana.fim, semana.agora)}</span>}
      </div>
      {semana?.premio && (
        <div className="cartao entrada-premio">
          <span className="premio-ico" aria-hidden="true"><IconeTrofeu /></span>
          <p><small>PRÊMIO DO TRIO CAMPEÃO</small><b>{semana.premio}</b></p>
        </div>
      )}
      <div className="entrada-progresso" role="status">
        <span>Entrando na mesa</span>
        <div className="trilho"><i style={{ animationDuration: `${ms}ms` }} /></div>
      </div>
    </div>
  );
}

export function IconeTrofeu() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4zM7 6H4v2a3 3 0 0 0 3 3M17 6h3v2a3 3 0 0 1-3 3" />
    </svg>
  );
}
